import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { History, Edit } from '@mui/icons-material'
import { useRefresh, useNotify, useDataProvider } from 'react-admin'
import { supabase } from '../supabaseClient'
import { Property } from '../types'
import { PropertyStatus } from '@repo/domain'

interface StatusColumnProps {
  record: Property & {
    status_code?: string
  }
}

interface StatusVersion {
  id: string
  status_code: string
  valid_from: string
  valid_to?: string | null
  created_at: string | null
}

const statusOptions = [
  { value: PropertyStatus.Available, label: 'Dostępne', color: 'success' as const },
  { value: PropertyStatus.Reserved, label: 'Zarezerwowane', color: 'warning' as const },
  { value: PropertyStatus.Sold, label: 'Sprzedane', color: 'error' as const },
  { value: PropertyStatus.ComingSoon, label: 'Wkrótce', color: 'default' as const },
]

export const StatusColumn: React.FC<StatusColumnProps> = ({ record }) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  const [changeOpen, setChangeOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [statusHistory, setStatusHistory] = useState<StatusVersion[]>([])
  const [newStatus, setNewStatus] = useState('')
  const [currentStatus, setCurrentStatus] = useState(record.status_code || '')
  const [loading, setLoading] = useState(false)
  const refresh = useRefresh()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    setCurrentStatus(record.status_code || '')
  }, [record.status_code])

  const getStatusLabel = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status)
    return statusOption?.label || status
  }

  const getStatusColor = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status)
    return statusOption?.color || 'default'
  }


  const loadStatusHistory = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('property_status_versions')
        .select('*')
        .eq('property_id', record.id)
        .order('valid_from', { ascending: false })

      if (error) throw error
      setStatusHistory(data || [])
    } catch (error) {
      console.error('Error loading status history:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleHistoryClick = () => {
    setHistoryOpen(true)
    loadStatusHistory()
  }

  const handleChangeStatusClick = () => {
    setNewStatus(currentStatus)
    setChangeOpen(true)
  }

  const handleStatusChangeConfirm = () => {
    setChangeOpen(false)
    setConfirmOpen(true)
  }

  const handleStatusUpdate = async () => {
    try {
      setLoading(true)

      // Use RPC function to update status
      const { data, error } = await supabase.rpc('update_property_status', {
        p_property_id: record.id,
        p_new_status: newStatus
      })

      if (error) throw error

      if (data?.success) {
        setCurrentStatus(newStatus)
        setConfirmOpen(false)
        setNewStatus('')

        refresh()
        notify('Status został zaktualizowany', { type: 'success' })
      } else {
        throw new Error('RPC returned unsuccessful result')
      }
    } catch (error) {
      console.error('Error updating status:', error)
      notify('Błąd podczas aktualizacji statusu', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      {/* Main status display */}
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          label={getStatusLabel(currentStatus)}
          color={getStatusColor(currentStatus)}
          size="small"
        />
        
        {/* Action buttons */}
        <Box display="flex" flexDirection="column">
          <Tooltip title="Historia statusów">
            <IconButton size="small" onClick={handleHistoryClick}>
              <History fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zmień status">
            <IconButton size="small" onClick={handleChangeStatusClick}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>Historia statusów - {record.title}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>Ładowanie...</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Od</TableCell>
                    <TableCell>Do</TableCell>
                    <TableCell>Utworzono</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusHistory.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(version.status_code)}
                          color={getStatusColor(version.status_code)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(version.valid_from).toLocaleDateString('pl-PL')}
                      </TableCell>
                      <TableCell>
                        {version.valid_to 
                          ? new Date(version.valid_to).toLocaleDateString('pl-PL')
                          : 'Aktualny'
                        }
                      </TableCell>
                      <TableCell>
                        {version.created_at ? new Date(version.created_at).toLocaleDateString('pl-PL') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        maxWidth={isMobile ? false : "sm"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>Zmiana statusu - {record.title}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <FormControl fullWidth>
              <InputLabel>Nowy status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Nowy status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Chip 
                      label={option.label}
                      color={option.color}
                      size="small"
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aktualny status: {getStatusLabel(currentStatus)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handleStatusChangeConfirm}
            variant="contained"
            disabled={!newStatus || newStatus === currentStatus}
          >
            Zmień status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth={isMobile ? false : "sm"}
        fullScreen={isMobile && false} // Confirmation dialog nie powinien być fullScreen
      >
        <DialogTitle>Potwierdź zmianę statusu</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz zmienić status z "{getStatusLabel(currentStatus)}" na "{getStatusLabel(newStatus)}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ta operacja zamknie aktualny status i utworzy nową wersję.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handleStatusUpdate}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Zapisywanie...' : 'Potwierdź'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}